// components/forms/DataMinerForm.tsx
'use client'

import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { profileState } from "@/atoms/profile";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CaretLeft, SignOut, Eye, ChatCircle, Activity } from "@phosphor-icons/react";
import { Avatar } from "@/components/Avatar";
import { signOut } from "@junobuild/core";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/utils";
import { Currency } from "@/types/entities";
import DocumentUpload from "@/components/upload/DocumentUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CaretDown } from "@phosphor-icons/react";
import { upload } from "@/components/data/upload";

type DataMinerFormProps = {
    totalIncome: number;
    totalExpense: number;
    selectedCurrency: string;
    setSelectedCurrency: (currency: string) => void;
    currencies?: Currency[][] | undefined;
};

export const DataMinerForm: React.FC<DataMinerFormProps> = ({ totalIncome, totalExpense, selectedCurrency, currencies }) => {
    const [profile] = useRecoilState(profileState);
    const navigate = useNavigate();
    const [exchangeRate, setExchangeRate] = useState(1);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [expandedOption, setExpandedOption] = useState<string | null>(null);
    const [selectedSubOption, setSelectedSubOption] = useState<string | null>(null);

    useEffect(() => {
        const getExchangeRate = (currency: string): number => {
            if (currency === "USD") return 1;
            const flattenedCurrencies = currencies?.flat();
            const currencyData = flattenedCurrencies?.find((curr) => curr.name === currency);
            return currencyData ? currencyData.value : 1;
        };

        const rate = getExchangeRate(selectedCurrency);
        console.log(`Exchange rate for ${selectedCurrency}:`, rate);
        setExchangeRate(rate);
    }, [selectedCurrency, currencies]);

    // Fallback currency
    const currencyCode = selectedCurrency || "USD";

    // adjust font size based on currency
    const adjustFontSize = (amount: number): string => {
        const digits = Math.abs(amount).toString().length;
        if (digits > 12) return 'text-[40px] sm:text-xs'; // Extremely small
        if (digits > 9) return 'text-[20px] sm:text-xs';     // Extra small
        if (digits > 6) return 'text-[10px] sm:text-xs';   // Small
        return 'text-base sm:text-lg';                   // Base (default)
    };

    // handle file upload
    const handleFileUpload = (file: File) => {
        console.log("File uploaded:", file);
        setIsUploadDialogOpen(false);
        setSelectedSubOption(null);
    };

    const handleUploadClick = (type: string) => {
        if (expandedOption === type) {
            setExpandedOption(null);
            setSelectedSubOption(null);
        } else {
            setExpandedOption(type);
            setSelectedSubOption(null);
        }
    };

    const handleSubOptionSelect = (subOption: string) => {
        setSelectedSubOption(subOption);
    };

    return (
        <div className="flex justify-center min-h-screen">
            <div className="w-full max-w-xl mx-auto">
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <div className="flex">
                            <CaretLeft className="w-6 h-6 text-white mt-1" onClick={() => navigate("/")} />
                            <CardTitle className="ml-4 text-xl">Profile</CardTitle>
                        </div>
                        <div className="flex gap-2">
                            <SignOut className="w-6 h-6" onClick={signOut} />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col items-center mt-8">
                        <div className="rounded-full overflow-hidden mb-4">
                            {profile ? (
                                <Avatar
                                    profile={profile}
                                    size="large"
                                    className="object-cover"
                                />
                            ) : (
                                <div className="w-32 h-32 bg-gray-600 flex items-center justify-center text-white text-3xl rounded-full">
                                    ?
                                </div>
                            )}
                        </div>
                        <div className="text-center mt-2">
                            <p className="text-xl font-bold text-white">{profile?.username || "Unknown User"}</p>
                            <p className="text-sm text-gray-400 mt-1">{profile?.bio || "No bio available"}</p>
                        </div>
                    </div>

                    <div className="flex justify-between mt-4 text-center">
                        <div className="bg-gray-800 px-2 py-1 rounded-lg flex-1 mx-1">
                            <div className="flex items-center justify-center mb-1">
                                <Activity className="w-4 h-4 text-white mr-1" />
                                <p className="text-xs text-gray-400">Income</p>
                            </div>
                            <span className={`${adjustFontSize(totalIncome)} text-green-500`}>
                                {formatCurrency(totalIncome, currencyCode)}
                            </span>
                        </div>
                        <div className="bg-gray-800 px-2 py-1 rounded-lg flex-1 mx-1">
                            <div className="flex items-center justify-center mb-1">
                                <Activity className="w-4 h-4 text-white mr-1" />
                                <p className="text-xs text-gray-400">Expense</p>
                            </div>
                            <span className={`${adjustFontSize(totalExpense)} text-red-500`}>
                                {formatCurrency(totalExpense, currencyCode)}
                            </span>
                        </div>
                        <div className="bg-gray-800 px-2 py-1 rounded-lg flex-1 mx-1">
                            <div className="flex items-center justify-center mb-1">
                                <Activity className="w-4 h-4 text-white mr-1" />
                                <p className="text-xs text-gray-400">Savings</p>
                            </div>
                            <span className={`${adjustFontSize(totalIncome - totalExpense)} text-gray-200`}>
                                {formatCurrency(totalIncome - totalExpense, currencyCode)}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-between mt-6">
                        <div className="flex items-start">
                            <Eye className="w-5 h-5 text-white mr-2 mt-1" />
                            <div>
                                <p className="text-sm font-semibold text-white">101 profile views</p>
                                <p className="text-xs text-gray-400">Discover who's viewed your profile.</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <ChatCircle className="w-5 h-5 text-white mr-2 mt-1" />
                            <div>
                                <p className="text-sm font-semibold text-white">210 search appearances</p>
                                <p className="text-xs text-gray-400">Discover who's viewed your profile.</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-center mt-8">
                        <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                            <DialogTrigger asChild>
                                <Button className="mt-4 w-full py-3 bg-[#352FF2] rounded-lg shadow-md hover:bg-[#4D5BE1] transition duration-200">
                                    Upload Data
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#1C1C1E] border-none p-8 w-80 max-w-[90vw] rounded-lg shadow-lg">
                                <DialogHeader>
                                    <DialogTitle className="text-white text-2xl font-semibold mb-6">Upload Data</DialogTitle>
                                </DialogHeader>
                                <div className="flex flex-col space-y-4">
                                    {upload.map((option) => (
                                        <div key={option.value}>
                                            <Button
                                                onClick={() => handleUploadClick(option.value)}
                                                className={`w-full justify-between text-white text-left py-3 px-4 rounded-lg transition duration-200 ${expandedOption === option.value ? 'bg-[#352FF2] rounded-b-none' : 'bg-[#352FF2] hover:bg-[#4D5BE1]'}`}
                                            >
                                                {option.label}
                                                <CaretDown className={`ml-2 h-5 w-5 transition-transform ${expandedOption === option.value ? 'rotate-180' : ''}`} />
                                            </Button>
                                            {expandedOption === option.value && (
                                                <div className="border border-[#4D5BE1] rounded-b-lg overflow-hidden">
                                                    {option.subOptions.map((subOption) => (
                                                        <div
                                                            key={subOption}
                                                            onClick={() => handleSubOptionSelect(subOption)}
                                                            className="w-full cursor-pointer justify-start text-white py-2 px-4 hover:bg-[#3A3A3C] transition duration-200 text-left"
                                                        >
                                                            {subOption}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {selectedSubOption && (
                                    <DocumentUpload
                                        onFileUpload={handleFileUpload}
                                        customMessage={`Upload ${selectedSubOption}`}
                                    />
                                )}
                            </DialogContent>
                        </Dialog>
                    </div>

                    <div className="mt-4">
                        <Tabs defaultValue="bank-statement" className="w-full">
                            <TabsList className="flex justify-between w-full bg-transparent">
                                <TabsTrigger
                                    value="bank-statement"
                                    className="flex-1 text-center px-2 py-2 text-xs font-medium text-gray-400 border-b-2 border-transparent data-[state=active]:text-white data-[state=active]:border-blue-500"
                                >
                                    Bank Statement
                                </TabsTrigger>
                                <TabsTrigger
                                    value="browsing-history"
                                    className="flex-1 text-center px-2 py-2 text-xs font-medium text-gray-400 border-b-2 border-transparent data-[state=active]:text-white data-[state=active]:border-blue-500"
                                >
                                    Browsing History
                                </TabsTrigger>
                                <TabsTrigger
                                    value="shopping-history"
                                    className="flex-1 text-center px-2 py-2 text-xs font-medium text-gray-400 border-b-2 border-transparent data-[state=active]:text-white data-[state=active]:border-blue-500"
                                >
                                    Shopping History
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent value="bank-statement">
                                <div className="mt-8">
                                    <p className="text-lg font-semibold">Bank Statement</p>
                                    <div className="mt-4 flex justify-between">
                                        <div className="text-sm text-[#A0A0AB]">
                                            <p>Bank Permata</p>
                                            <p>Bank BCA</p>
                                            <p>Bank BRI</p>
                                        </div>
                                        <div className="text-right text-sm">
                                            <p>$4,430</p>
                                            <p>$4,430</p>
                                            <p>$4,430</p>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value="browsing-history">
                                <div className="mt-8">
                                    <p className="text-lg font-semibold">Browsing History</p>
                                    <div className="mt-4 flex justify-between">
                                        <div className="text-sm text-[#A0A0AB]">
                                            <p>Website name</p>
                                            <p>Website usage description</p>
                                            <p>Another description</p>
                                        </div>
                                        <div className="text-right text-sm">
                                            <p>5 hours</p>
                                            <p>3 hours</p>
                                            <p>2 hours</p>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                            <TabsContent value="shopping-history">
                                <div className="mt-8">
                                    <p className="text-lg font-semibold">Shopping History</p>
                                    <div className="mt-4 flex justify-between">
                                        <div className="text-sm text-[#A0A0AB]">
                                            <p>Store name</p>
                                            <p>Items purchased</p>
                                            <p>More items</p>
                                        </div>
                                        <div className="text-right text-sm">
                                            <p>$453</p>
                                            <p>$120</p>
                                            <p>$75</p>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                    <div className="mt-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">Data profile</h2>
                            <span className="text-sm text-gray-400">4</span>
                        </div>
                        {[1, 2, 3, 4].map((item, index) => (
                            <div key={index} className="flex items-center mb-4">
                                <div className="w-12 h-12 bg-gray-700 rounded-lg mr-4"></div>
                                <div className="flex-grow">
                                    <p className="text-sm text-gray-400">101 Coffee Shop</p>
                                    <p className="text-base font-semibold">Black Coffee</p>
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm font-semibold">$40</p>
                                        <p className="text-xs text-gray-400">May 2024</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </div>
        </div>
    );
};